import { useMemo, useCallback } from 'react'
import { primaryTargetGroupLabels } from '../../helpers/dataHelpers/targetgroupCategories'
import { activityTypeLabels } from '../../helpers/dataHelpers/activityCategories'
import { mechanismCategoryLabels } from '../../helpers/dataHelpers/mechanismCategories'
import buildSectorPathsSankey from '../../helpers/dataHelpers/buildSectorPathsSankey'
import SectorTocPathsSankey from '../Visualisations/sectorPaths/SectorTocPathsSankey'
import telwoord from '../../helpers/intelligence/telwoord'

/**
 * TOC-paden van één thema: welke paden (doelgroep → activiteit → mechanisme →
 * effect) organisaties ontwerpen richting dít effect voor déze doelgroep.
 *
 * Het model komt server-side gefilterd binnen (useThemeTocPaths); hier wordt
 * alleen getekend. Zelfde Sankey en zelfde labelherkomst als de
 * sectorverkenner, zodat een pad hier hetzelfde heet als daar.
 * AI-CONTEXT: See docs/pitfalls.md#dev-copied-recipe-instead-of-shared-hook
 *
 * De grafiekwrapper heeft in CSS een opgegeven hoogte (`sector-paths-chart`):
 * de Sankey staat op 100% en lost anders op naar 0.
 * AI-CONTEXT: See docs/pitfalls.md#ui-chart-renders-blank-after-move
 *
 * @param {object} props
 * @param {object} props.theme - Het thema; levert de effectnaam voor het label
 * @param {{model: (object|null), loading: boolean, loaded: boolean, error: (Error|null)}} props.paths - Uit useThemeTocPaths
 */
const ThemeTocPathsSection = ({ theme, paths }) => {
    const effectName = (theme && theme.effectName) || ''
    const msiId = (theme && theme.msiId) || ''

    const resolveLabel = useCallback((column, rawKey) => {
        if (column === 'primaryTargetGroup') return primaryTargetGroupLabels[rawKey] || rawKey
        if (column === 'activityType') return activityTypeLabels[rawKey] || rawKey
        if (column === 'mechanismCategory') return mechanismCategoryLabels[rawKey] || rawKey
        if (column === 'effect') return (rawKey === msiId && effectName) ? effectName : rawKey
        return rawKey
    }, [msiId, effectName])

    const sankey = useMemo(() => buildSectorPathsSankey(paths.model, resolveLabel), [paths.model, resolveLabel])

    const inhoud = () => {
        if (paths.error) {
            return (
                <p className="intelligence-state intelligence-state--error">
                    De TOC-paden konden niet worden geladen: {paths.error.message}
                </p>
            )
        }
        if (!paths.loaded || !paths.model) return <p className="intelligence-state">TOC-paden laden…</p>
        if (sankey.isEmpty) {
            return (
                <p className="intelligence-state">
                    Nog geen volledig pad (doelgroep → activiteit → mechanisme → effect) voor deze
                    doelgroep op dit effect.
                </p>
            )
        }
        return (
            <>
                <p className="intelligence-agenda__meta">
                    {telwoord(paths.model.pathCount, 'pad', 'paden')} · {telwoord(paths.model.companyCount, 'organisatie', 'organisaties')}
                </p>
                <div className="sector-paths-chart">
                    <SectorTocPathsSankey
                        nodes={sankey.nodes}
                        links={sankey.links}
                        columns={paths.model.columns || []}
                        selected={{}}
                        onNodeClick={() => {}}
                    />
                </div>
            </>
        )
    }

    return (
        <section className="intelligence-panel">
            <h3 className="intelligence-panel__title">TOC-paden</h3>
            {inhoud()}
        </section>
    )
}

export default ThemeTocPathsSection
